import {
  type ApiClient,
  type ApiError,
  type CatalogueItemResource,
  OutletCatalogueController_read_v1,
  type OutletResource,
} from '@repo/api-client';
import type { ReactNode } from 'react';
import { Link } from 'react-router';

import { collectPages } from '@/api/collect-pages';
import { messageForFailure } from '@/api/failure';

import { outletCataloguePath } from '../paths';

/**
 * The fourth door into the pair FR-208 forbids, answered from the outlet's side.
 *
 * `PATCH /v1/outlets/{id}` with `carriesStock: false` is refused —
 * `CATALOGUE_STOCK_NOT_AVAILABLE_HERE`, the same 409 the availability routes
 * raise — while that outlet still sells a stock-tracked item. It used to be the
 * one path that could produce the forbidden pair from the other end; it is now
 * shut, and the refusal lands on `EditOutletDialog`, which is a screen about
 * outlets and knows nothing about the catalogue.
 *
 * **Left to the generic copy this reads as an arbitrary refusal on an unrelated
 * form.** "One of the outlets chosen does not carry stock" is a sentence about
 * outlets, and the person reading it did not choose any outlets — they turned off
 * a switch. So this file lives with the availability screens, is imported by that
 * dialog, and says the two things the generic message cannot: *which* items are
 * still sold here, and that withdrawing them is a change made on the item rather
 * than on this form.
 *
 * ## The items are named from a read, because the refusal does not name them
 *
 * `assertNothingStockTrackedIsSoldHere` selects the offending row and then throws
 * `new AppException('CATALOGUE_STOCK_NOT_AVAILABLE_HERE')` with no second
 * argument, so nothing about which item comes back — the same gap the create and
 * the edit dialogs already record for the other direction, and reported again in
 * this PR body.
 *
 * `GET /v1/outlets/{id}/catalogue` answers it instead. It is read whole here
 * rather than paged, which is the opposite of what the screen behind it does, and
 * deliberately: this runs once, on a refusal, to write one sentence that needs
 * every stock-tracked item sold here — not on the ordinary path where the same
 * list grows with the catalogue and is paged.
 *
 * **Every failure falls back to the rule rather than throwing.** This runs inside
 * a `catch` that is already handling a refusal, and a second failure must not
 * replace it with a network error about a request the person never made. The
 * fallback is `error-messages.ts`'s copy for the code, because no screen writes
 * its own refusal.
 *
 * ## Two ways the read can honestly disagree with the refusal
 *
 * It answers with **live** items only, and the API's check does not exclude
 * retired ones — their availability rows stay live, because withdrawing is what
 * removes one and retiring the item is not. So an outlet whose only stock-tracked
 * item has been retired is refused by a rule this read cannot see the reason for.
 * The stock position can also have changed between the two calls. Both land on
 * the fallback, which states the rule and is true in every case.
 */
export async function outletStockRefusal(
  client: ApiClient,
  outlet: OutletResource,
  refusal: ApiError,
): Promise<ReactNode> {
  try {
    const sold = await collectPages<CatalogueItemResource>((offset, limit) =>
      client.request(OutletCatalogueController_read_v1, {
        path: { id: outlet.id },
        // Only a `PRODUCT` may ever count stock (FR-212), so this is the whole
        // of what the refusal can be about and the narrower question to ask.
        query: { offset, limit, kind: 'PRODUCT' },
      }),
    );

    const counted = sold.filter((item) => item.tracksStock);

    if (counted.length === 0) return messageForFailure(refusal);

    const one = counted.length === 1;

    return (
      <>
        {outlet.name} still sells {namesOf(counted)}, {one ? 'which counts' : 'which count'} stock.
        An outlet that holds no stock cannot sell an item that counts it, so nothing was changed —
        the switch is still on. Withdraw {one ? 'it' : 'them'} from this outlet first, under{' '}
        <span className="whitespace-nowrap">&ldquo;Where it sells&rdquo;</span> on the item, then
        turn this off.{' '}
        <Link
          to={outletCataloguePath(outlet.id)}
          className="rounded-sm underline underline-offset-4 outline-none focus-visible:ring-3 focus-visible:ring-ring/30"
        >
          Everything {outlet.name} sells
        </Link>
        .
      </>
    );
  } catch {
    return messageForFailure(refusal);
  }
}

/**
 * The items, as a sentence, with a ceiling on how many are named.
 *
 * An outlet with sixty stock-tracked products would otherwise produce a refusal
 * nobody finishes reading, and the names past the first few are not what makes
 * the sentence actionable — the link below it is.
 */
function namesOf(items: readonly CatalogueItemResource[]): string {
  const NAMED = 4;

  const names = items.slice(0, NAMED).map((item) => item.name);
  const rest = items.length - names.length;

  return new Intl.ListFormat('en-IN', { type: 'conjunction' }).format(
    rest === 0 ? names : [...names, `${String(rest)} other${rest === 1 ? '' : 's'}`],
  );
}
